import { z } from 'zod';

import { richDocSchema, storedRichDocSchema } from './rich-text';
import { slugSchema } from './store';

/**
 * Memorial Grow uP (decidido pelo GrowuP a 19-10-2026): homenagens a antigos
 * membros que ja nos deixaram, em `growup.gg/memorial`. Gere-se em
 * Conteudos > Memorial (`content.manage`). Ver docs/conteudos.
 *
 * Prefixos HTTP:
 *   - `/api/memorial/...`               site publico;
 *   - `/api/site/content/memorial/...`  back-office, so em gestao.
 */

/** Uma imagem carregada (`/api/media/...`); nada de imagens de terceiros. */
const mediaSrcSchema = z
  .string()
  .trim()
  .regex(/^\/api\/media\/[0-9a-f-]{36}\/(sm|md|lg)\.webp$/, 'carregue a foto no back-office');

const yearSchema = z.number().int().min(1990).max(2200).nullable().default(null);

/** `draft`: so no back-office; `published`: no site. */
export const tributeStatusSchema = z.enum(['draft', 'published']);
export type TributeStatus = z.infer<typeof tributeStatusSchema>;

/** Um cartao da pagina `/memorial`. */
export const publicTributeSummarySchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  nickname: z.string(),
  /** Vazio = so a alcunha (o nome so aparece com autorizacao da familia). */
  name: z.string(),
  photo: z.object({ url: z.string(), alt: z.string() }).nullable(),
  yearFrom: z.number().int().nullable(),
  yearTo: z.number().int().nullable(),
});
export type PublicTributeSummary = z.infer<typeof publicTributeSummarySchema>;

export const publicTributeSchema = publicTributeSummarySchema.extend({
  body: storedRichDocSchema,
});
export type PublicTribute = z.infer<typeof publicTributeSchema>;

export const tributeInputSchema = z
  .object({
    slug: slugSchema,
    nickname: z.string().trim().min(1, 'escreva a alcunha').max(60),
    name: z.string().trim().max(120).default(''),
    photoUrl: mediaSrcSchema.nullable().default(null),
    photoAlt: z.string().trim().max(300).default(''),
    yearFrom: yearSchema,
    yearTo: yearSchema,
    body: richDocSchema,
    /** Quem da familia autorizou (texto livre, so no back-office). */
    consentBy: z.string().trim().max(200).default(''),
    /** Quando autorizou (`aaaa-mm-dd`). */
    consentOn: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable()
      .default(null),
  })
  .refine((t) => t.photoUrl === null || t.photoAlt !== '', {
    message: 'descreva a foto (para quem nao a ve)',
    path: ['photoAlt'],
  })
  .refine((t) => t.yearFrom === null || t.yearTo === null || t.yearTo >= t.yearFrom, {
    message: 'o ano de saida tem de ser depois do de entrada',
    path: ['yearTo'],
  });
export type TributeInput = z.input<typeof tributeInputSchema>;

export const adminTributeSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  nickname: z.string(),
  name: z.string(),
  photoUrl: z.string().nullable(),
  photoAlt: z.string(),
  yearFrom: z.number().int().nullable(),
  yearTo: z.number().int().nullable(),
  body: storedRichDocSchema,
  consentBy: z.string(),
  consentOn: z.string().nullable(),
  status: tributeStatusSchema,
  sortOrder: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Login de quem escreveu e de quem mexeu por ultimo. */
  createdBy: z.string(),
  updatedBy: z.string(),
});
export type AdminTribute = z.infer<typeof adminTributeSchema>;

export const tributeOrderRequestSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(500),
});

/** `2014–2021`, `Membro desde 2014`, `Membro até 2021` ou nada. */
export function tributeYears(t: { yearFrom: number | null; yearTo: number | null }): string {
  if (t.yearFrom !== null && t.yearTo !== null) {
    return t.yearFrom === t.yearTo
      ? String(t.yearFrom)
      : `${String(t.yearFrom)}–${String(t.yearTo)}`;
  }
  if (t.yearFrom !== null) return `Membro desde ${String(t.yearFrom)}`;
  if (t.yearTo !== null) return `Membro até ${String(t.yearTo)}`;
  return '';
}
